'use client'

import { useEffect } from 'react'
import { CookieBanner, ConsentProvider, useConsent } from '@unity/cookie-consent'
import { AnalyticsProvider } from '@unity/analytics'
import { initSentryIfConsented, closeSentry } from '../../sentry.client.config'

/**
 * W2.1 P0 fix (S9.A.01 Finding 2):
 * Sentry.init is gated on consent.analytics. This watcher subscribes to
 * the ConsentProvider's state and lazy-initializes Sentry only after the
 * user grants analytics consent. If consent is later revoked, Sentry is
 * flushed + closed.
 *
 * Implementation note: closeSentry returns a Promise; we fire-and-forget
 * in the cleanup path because React effect cleanup is sync. The 2s flush
 * timeout in closeSentry bounds the orphaned promise.
 */
function SentryConsentGate() {
  const { consent } = useConsent()
  const analyticsGranted = consent?.categories.analytics === true

  useEffect(() => {
    if (analyticsGranted) {
      initSentryIfConsented(true)
    } else if (consent !== null) {
      // Consent loaded AND analytics not granted (or revoked) — ensure no
      // Sentry client is running. closeSentry is a no-op if not initialized.
      void closeSentry()
    }
  }, [analyticsGranted, consent])

  return null
}

/**
 * W2.1 P0 fix (S9.A.01 Finding 1):
 * CookieBanner internally calls useConsent() which throws if no
 * ConsentProvider wraps the tree. Mounting CookieBanner without the
 * provider crashed client hydration (caught silently by Next.js error
 * boundary), so the banner never rendered on shop.veterans.pro — a GDPR
 * Art. 7(1) breach. Wrapping in ConsentProvider restores the banner.
 */
export function ClientConsent() {
  return (
    <ConsentProvider>
      <SentryConsentGate />
      <AnalyticsProvider appName="veterans-shop">
        <CookieBanner />
      </AnalyticsProvider>
    </ConsentProvider>
  )
}
